/* eslint angular/controller-as-vm: 0 */
/** In this case, ctrl = BasePageController and vm = ChildrenController. */

/**
 * @typedef {Object} BaseController
 *
 * @property {{viewUrl: string, urlPath: string}} contentRequestParams
 * @property {string[]} requiredContentAttributes
 * @property {function} activate
 * @property {function} prepareSubpagesFromResponse
 * @property {function} preProcessResponse
 * @property {function} postProcessResponse
 * @property {function} preProcessError
 * @property {function} postProcessError
 */

(function() {
    'use strict';

    angular.module( APP_ID )
        .controller( 'BasePageController', [ 'vm', 'logger', 'BlockRecipesBuilder', 'SubpagesService', 'DataService', 'MetaService', 'LoadingManager', 'ErrorManager', 'Utils', BasePageController ]);


    /**
     * Base controller's functionality for a generic page with
     * some subpages in it.
     *
     * @param {angular.IController} vm
     * @param {Logger} logger
     * @param {BlockRecipesBuilder} BlockRecipesBuilder
     * @param {SubpagesService} SubpagesService
     * @param {DataService} DataService
     * @param {MetaService} MetaService
     * @param {LoadingManager} LoadingManager
     * @param {ErrorManager} ErrorManager
     * @param {Utils} Utils
     */
    function BasePageController(
        vm,
        logger,
        BlockRecipesBuilder,
        SubpagesService,
        DataService,
        MetaService,
        LoadingManager,
        ErrorManager,
        Utils
    ){
        /** @type {angular.IController} */
        var ctrl = this;





        /* ===== BASE CONTROLLER'S API ====================================== */


        // Members
        ctrl.contentRequestParams     = {
            viewUrl: null,
            urlPath: null
        };

        // Validator will check these top-level attributes are available in the response. If unavailable, a critical error will be thrown.
        ctrl.requiredContentAttributes = [
            'metadata',
            'sharedAssets'
        ];

        // Methods
        ctrl.activate                    = activate; // Startup (~ super) -> *MUST* be called!
        ctrl.prepareSubpagesFromResponse = prepareSubpagesFromResponse;

        // Flow Hooks
        /** @type {function(object)} */
        ctrl.preProcessResponse          = null; // function( responseObject ) {};

        /** @type {function(object)} */
        ctrl.postProcessResponse         = null; // function( responseObject ) {};

        /** @type {function(object)} */
        ctrl.preProcessError             = null; // function( errorObject ) {};

        /** @type {function(object)} */
        ctrl.postProcessError            = null; // function( errorObject ) {};





        /* ===== CONTROLLER'S PUBLIC API ==================================== */

        // Members (+ their defaults)
        vm.isContentReady   = false;

        vm.content          = {};

        /** @type {Object} */
        vm.sharedContent    = undefined;

        /** @type {Object} */
        vm.metadata         = undefined;





        /* ===== RUN ON INSTANTIATION ======================================= */


        /** @constructor */
        (function init(){
            LoadingManager.loadingPending();
            BlockRecipesBuilder.initRecipesFromComponents(); // Can be moved to core/startup.js if needed everywhere!

            SubpagesService.currentSubpages = null;

            Utils.setRootVmReference( vm );
        })();





        /* ===== BASE CONTROLLER'S INHERITANCE METHODS ====================== */


        /**
         * Makes an API request and binds a response to generic properties.
         */
        function activate(){
            defaultSettings();
            requestAndBindContent();
        }





        /* ===== PRIVATE FUNCTIONS ========================================== */


        /** Makes API request to retrieve content. */
        function requestAndBindContent() {
            if ( !ctrl.contentRequestParams.viewUrl ) {
                ErrorManager.handleError(
                    'URL path/parameter is missing.',
                    'CMS_RESOURCE_NOT_FOUND',
                    { responseCode: '404' }
                );
                LoadingManager.loadingAborted();
            } else {
                DataService.getContent( ctrl.contentRequestParams )
                    .then( processResponse )
                    .catch( processError );
            }
        }


        /** Turns all the shared settings into its default state and resets metadata. */
        function defaultSettings() {
            MetaService.resetToDefaults();
            SubpagesService.setSelectableAttribute( null );
        }


        /**
         * Validates and processes an Object with page's content. Aditionally
         * runs pre- and post- processing hooks that can be defined in the
         * child (~inheriting) Controller.
         *
         * @param {Object} responseObject
         */
        function processResponse( responseObject ) {
            // 1. Validate the response and check requried parameters are there
            if ( !Utils.validateCmsResponse( responseObject, ctrl.requiredContentAttributes )){
                LoadingManager.loadingAborted();
                return;
            }

            // 2. Optional pre-processing hook
            if ( ctrl.preProcessResponse ) ctrl.preProcessResponse( responseObject );

            // 3. Standard processing
            vm.sharedContent    = responseObject.sharedAssets;
            vm.metadata         = responseObject.metadata;

            /**
             * Settings that can be propagated down the dynamic blocks
             * generator as a `customData` and wired to custom block
             * component's binding through 'renderingOptions' attribute.
             */
            vm.dynamicBlocksSettings = {};
            vm.dynamicBlocksSettings.metadata = vm.metadata;

            var metadataDescription, pageName;

            if ( vm.metadata.hasOwnProperty( 'description' ) ){
                metadataDescription = ( vm.metadata.description !== 'null' ) ? vm.metadata.description : null;
            } else {
                logger.error( "A property `description` is missing from `metadata`. Cannot properly render SEO-friendly meta description.", vm.metadata );
            }

            if ( vm.metadata.hasOwnProperty( 'name' ) ){
                pageName = vm.metadata.hasOwnProperty( 'fullName' ) ? vm.metadata.fullName : vm.metadata.name;
            } else {
                logger.error( "A property `name` is missing from `metadata`. Cannot properly render SEO-friendly meta title and description.", vm.metadata );
            }

            prepareSearchMetadata();

            MetaService.updateTitleStructure({
                page: pageName
            });

            MetaService.updateDescriptionStructure({
                page:   pageName,
                suffix: metadataDescription
            });

            Utils.setTooltipsLibrary( vm.sharedContent.toolTips.asset_contents );
            Utils.setSharedContent( vm.sharedContent );

            // 4. Optional post-processing hook
            if ( ctrl.postProcessResponse ) ctrl.postProcessResponse( responseObject );

            LoadingManager.loadingDone();
            vm.isContentReady = true;
        }

        function prepareSearchMetadata() {
            // Funnelback search-related info
            var hasDomFees = false;

            if ( vm.metadata.hasOwnProperty( 'searchKeywords' ) ){
                MetaService.searchAttributes.push({
                    name:  'keywords',
                    value: vm.metadata.searchKeywords,
                });
            }

            if ( vm.metadata.hasOwnProperty( 'childType' ) && vm.metadata.childType === 'programme'){
                // all data comes from

                if ( vm.hasOwnProperty( 'content' ) && vm.content.hasOwnProperty( 'AboutBlocks' ) && vm.content.AboutBlocks.length > 0) {
                    vm.content.AboutBlocks.forEach(function (blockKey){
                        if ( blockKey.contentType === "atAGlance"){
                            if ( blockKey.hasOwnProperty( 'asset_contents' ) && blockKey.asset_contents.hasOwnProperty('qualifications') && blockKey.asset_contents.qualifications.length > 0){
                                // Always look at parent qual. Leans on structure from CMS
                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'code' )){
                                    MetaService.searchAttributes.push({
                                        name:  'code',
                                        value: blockKey.asset_contents.qualifications[0].metadata.code,
                                    });

                                    MetaService.searchAttributes.push({
                                        name:  'abbreviation',
                                        value: blockKey.asset_contents.qualifications[0].metadata.code,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'internationalApiCode' )){
                                    MetaService.searchAttributes.push({
                                        name:  'degreeCode',
                                        value: blockKey.asset_contents.qualifications[0].metadata.internationalApiCode,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'flexibleStartDate' )){
                                    MetaService.searchAttributes.push({
                                        name:  'flexibleStartDate',
                                        value: blockKey.asset_contents.qualifications[0].metadata.flexibleStartDate,
                                    });
                                }
                                
                                /* This data can only be set on the parent as only the parents metadata is shown in head of page. See common metadata below for new placement. TODO remove after release
                                
                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'trimesterStart' )){
                                    MetaService.searchAttributes.push({
                                        name:  'trimesterStart',
                                        value: blockKey.asset_contents.qualifications[0].metadata.trimesterStart,
                                    });
                                }
                                
                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'relatedFaculty' )){
                                    MetaService.searchAttributes.push({
                                        name:  'relatedFaculty',
                                        value: blockKey.asset_contents.qualifications[0].metadata.relatedFaculty,
                                    });
                                }
                                */

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'durationMinTrimesters' )){
                                    MetaService.searchAttributes.push({
                                        name:  'minTrimesters',
                                        value: blockKey.asset_contents.qualifications[0].metadata.durationMinTrimesters,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'location' )){
                                    MetaService.searchAttributes.push({
                                        name:  'location',
                                        value: blockKey.asset_contents.qualifications[0].metadata.location,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'points' )){
                                    MetaService.searchAttributes.push({
                                        name:  'points',
                                        value: blockKey.asset_contents.qualifications[0].metadata.points,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'fees' ) && blockKey.asset_contents.qualifications[0].metadata.fees.hasOwnProperty( 'tuition' ) && blockKey.asset_contents.qualifications[0].metadata.fees.tuition !== 0){
                                    MetaService.searchAttributes.push({
                                        name:  'domestic-fees',
                                        value: blockKey.asset_contents.qualifications[0].metadata.fees.levies + blockKey.asset_contents.qualifications[0].metadata.fees.tuition + blockKey.asset_contents.qualifications[0].metadata.fees.extraCosts,
                                    });
                                    hasDomFees = true;

                                    MetaService.searchAttributes.push({
                                        name:  'domestic-fees-term',
                                        value: blockKey.asset_contents.qualifications[0].metadata.fees.period,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'internationalFeesTotal' )){

                                    MetaService.searchAttributes.push({
                                        name:  'international-fees',
                                        value: blockKey.asset_contents.qualifications[0].metadata.internationalFeesTotal + blockKey.asset_contents.qualifications[0].metadata.internationalFeesLevy,
                                    });


                                    MetaService.searchAttributes.push({
                                        name:  'international-fees-term',
                                        value: blockKey.asset_contents.qualifications[0].metadata.internationalFeesTerm,
                                    });

                                    MetaService.searchAttributes.push({
                                        name:  'international-fees-duration',
                                        value: blockKey.asset_contents.qualifications[0].metadata.internationalFeesDuration,
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'entryRequirements' )){
                                    MetaService.searchAttributes.push({
                                        name:  'entry-requirements',
                                        value: blockKey.asset_contents.qualifications[0].metadata.entryRequirements + ( blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'entryRequirementsNote' ) ? ' ' + blockKey.asset_contents.qualifications[0].metadata.entryRequirementsNote : '' ),
                                    });
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'keyDatesSet' ) && blockKey.asset_contents.qualifications[0].metadata.keyDatesSet.length > 0 ) {

                                    // var nextStartDate = Utils.getNextStartDateChronologically( blockKey.asset_contents.qualifications[0].metadata.keyDatesSet );
                                    var nextStartDate = Utils.returnAllStartDates( blockKey.asset_contents.qualifications[0].metadata.keyDatesSet );

                                    if ( nextStartDate ) {
                                        MetaService.searchAttributes.push({
                                            name:  'next-starting-date',
                                            value: nextStartDate,
                                        });
                                    }
                                }

                                if ( blockKey.asset_contents.qualifications[0].hasOwnProperty( 'metadata' ) && blockKey.asset_contents.qualifications[0].metadata.hasOwnProperty( 'durationNote' ) && blockKey.asset_contents.qualifications[0].metadata.durationNote !== '' ) {
                                    MetaService.searchAttributes.push({
                                        name:  'duration',
                                        value: blockKey.asset_contents.qualifications[0].metadata.durationNote,
                                    });
                                }
                            }
                        }
                    });
                }
            }
            else {

                if ( vm.metadata.hasOwnProperty( 'flexibleStartDate' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'flexibleStartDate',
                        value: vm.metadata.flexibleStartDate,
                    });
                }

                
                if ( vm.metadata.hasOwnProperty( 'durationMinTrimesters' ) && vm.metadata.durationMinTrimesters !== '') {
                    MetaService.searchAttributes.push({
                        name:  'minTrimesters',
                        value: vm.metadata.durationMinTrimesters,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'points' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'points',
                        value: vm.metadata.points,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'durationNote' ) && vm.metadata.durationNote !== '' ) {
                    MetaService.searchAttributes.push({
                        name:  'duration',
                        value: vm.metadata.durationNote,
                    });
                }
                else if (vm.metadata.hasOwnProperty( 'duration' ) && vm.metadata.duration !== '' ) {
                    MetaService.searchAttributes.push({
                        name:  'duration',
                        value: vm.metadata.duration,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'location' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'location',
                        value: vm.metadata.location,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'code' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'code',
                        value: vm.metadata.code,
                    });
                    MetaService.searchAttributes.push({
                        name:  'abbreviation',
                        value: vm.metadata.code,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'internationalApiCode' ) && vm.metadata.internationalApiCode !== "" ) {
                    MetaService.searchAttributes.push({
                        name:  'degreeCode',
                        value: vm.metadata.internationalApiCode,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'fees' ) && vm.metadata.fees.hasOwnProperty( 'tuition' ) && vm.metadata.fees.tuition !== 0) {
                    // Top level qual with subjects, set up correctly

                    MetaService.searchAttributes.push({
                        name:  'domestic-fees',
                        value: vm.metadata.fees.levies + vm.metadata.fees.tuition + vm.metadata.fees.extraCosts,
                    });

                    MetaService.searchAttributes.push({
                        name:  'domestic-fees-term',
                        value: vm.metadata.fees.period,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'entryRequirements' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'entry-requirements',
                        value: vm.metadata.entryRequirements + ( vm.metadata.hasOwnProperty( 'entryRequirementsNote' ) ? ' ' + vm.metadata.entryRequirementsNote : '' ),
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'keyDatesSet' ) && vm.metadata.keyDatesSet.length > 0 ) {
                    // var nextStartDate = Utils.getNextStartDateChronologically( vm.metadata.keyDatesSet ); // returns next single start date
                    var nextStartDate = Utils.returnAllStartDates( vm.metadata.keyDatesSet );

                    if ( nextStartDate ) {
                        MetaService.searchAttributes.push({
                            name:  'next-starting-date',
                            value: nextStartDate,
                        });
                    }
                }
            }

            // Common metadata
            // string null is intentional
            if ( vm.metadata.hasOwnProperty( 'relatedFaculty' )  &&  vm.metadata.relatedFaculty !== "" &&  vm.metadata.relatedFaculty !== "null" ) {
                MetaService.searchAttributes.push({
                    name:  'relatedFaculty',
                    value: vm.metadata.relatedFaculty,
                });
            }
            
            // string null is intentional
            if ( vm.metadata.hasOwnProperty( 'trimesterStart' ) &&  vm.metadata.trimesterStart !== "" &&  vm.metadata.trimesterStart !== "null") {
                MetaService.searchAttributes.push({
                    name:  'trimesterStart',
                    value: vm.metadata.trimesterStart,
                });
            }

            // International fees on parent qualification if present. If not present here, they would already have been created above as they must be present at some level
            if ( vm.metadata.hasOwnProperty( 'internationalFeesTotal' ) ) {
                MetaService.searchAttributes.push({
                    name:  'international-fees',
                    value: vm.metadata.internationalFeesTotal + vm.metadata.internationalFeesLevy,
                });

                if ( vm.metadata.hasOwnProperty( 'internationalFeesTerm' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'international-fees-term',
                        value: vm.metadata.internationalFeesTerm,
                    });
                }

                if ( vm.metadata.hasOwnProperty( 'internationalFeesDuration' ) ) {
                    MetaService.searchAttributes.push({
                        name:  'international-fees-duration',
                        value: vm.metadata.internationalFeesDuration,
                    });
                }
            }
            if ( vm.metadata.hasOwnProperty( 'id' ) && vm.metadata.hasOwnProperty( 'type' ) ) {
                MetaService.searchAttributes.push({
                    name:  'page-id',
                    value: vm.metadata.type + '/' + vm.metadata.id,
                });
                // add canonical URL information

                if (vm.metadata.type === "predegree" || vm.metadata.type === "undergrad"){
                    MetaService.addCanonicalUrl ( baseConfig.env.urls.website + '/explore/degrees/' + vm.metadata.id + '/overview' );
                }
                else if (vm.metadata.type === "topic" || vm.metadata.type === "megatopic"){
                    MetaService.addCanonicalUrl ( baseConfig.env.urls.website + '/explore/study-areas/' + vm.metadata.id + '/overview' );
                }
                else {
                    MetaService.addCanonicalUrl ( baseConfig.env.urls.website + '/explore/' + vm.metadata.type + '/' + vm.metadata.id + '/overview' );
                }
            }

            if ( vm.metadata.hasOwnProperty( 'teaser' ) ) {
                MetaService.searchAttributes.push({
                    name:  'abstract',
                    value: vm.metadata.teaser,
                });
            }

            if ( vm.metadata.hasOwnProperty( 'hasCerts' ) && vm.metadata.hasCerts == true){
                MetaService.searchAttributes.push({
                    name:  'has-certs',
                    value: vm.metadata.hasCerts,
                });
            }

            if ( vm.metadata.hasOwnProperty( 'hasDips' ) && vm.metadata.hasDips == true){
                MetaService.searchAttributes.push({
                    name:  'has-dips',
                    value: vm.metadata.hasDips,
                });
            }

            if ( vm.metadata.hasOwnProperty( 'type' ) ) {
                MetaService.searchAttributes.push({
                    name:  'level-of-study',
                    value: vm.metadata.type,
                });
            }
        }


        /**
         * Logs an encountered error through the App's {ErrorManager} and
         * allows for additional post-processing through hooks.
         *
         * @param {Object} errorObject
         */
        function processError( errorObject ) {
            if ( ctrl.preProcessError ) ctrl.preProcessError( errorObject );

            logger.error( "Response from CMS was received with an error or cannot be processed correctly: `%s`.", errorObject );

            ErrorManager.handleError( errorObject, 'CMS_RESOURCE_NOT_FOUND' );

            if ( ctrl.postProcessError ) ctrl.postProcessError( errorObject );

            LoadingManager.loadingAborted();
        }


        /**
         * Takes an Object with all content data and prepares a model
         * for subpages and tabbed menu.
         *
         * @param {Object} responseObject
         */
        function prepareSubpagesFromResponse( responseObject ){
            var subpagesIds = Object.keys( responseObject.pages ), subpageBlocksId,
            hasReferences = responseObject.hasOwnProperty( 'references' );

            SubpagesService.setSubpagesFromIds( subpagesIds );

            //Start subpage checking.. - TOOD - abstract into method?
            if( !checkForSubPages( SubpagesService.currentSubpages, SubpagesService.getCurrentSubpageInfo() ) ) {
                ErrorManager.handleError(
                    'Subpage doesnt exist for qualification',
                    'CMS_RESOURCE_NOT_FOUND',
                    { responseCode: '404' }
                );

                //Set Loading states
                LoadingManager.loadingAborted();
                return false;
            }

            // Map subpages info to content block sets.
            subpagesIds.forEach( function( subpageId ){
                subpageBlocksId = subpageId + 'Blocks';

                vm.content[ subpageBlocksId ] = responseObject.pages[ subpageId ].asset_contents;
                if ( hasReferences ) Utils.bindDynamicBlockReferences( responseObject[ 'references' ], vm.content[ subpageBlocksId ]);
            });
        }

        /**
         * Takes data from Subpage service to determine if current route is valid
         *
         * @param {Array} currentSubPages
         * @param {Object} state
         */
        function checkForSubPages(currentSubPages, state) {
            var matches = currentSubPages.map(function(x) {
                return x.data.subpage.id
            }).filter(function(x) {
                return x == state.id;
            });

            return matches.length;
        }
    }

})();
